import * as fs from "@tauri-apps/plugin-fs";
import { isEmpty } from "lodash";
import sql, { type Sql } from "sql-template-tag";
import { assign, fromPromise, setup } from "xstate";
import { z } from "zod";

import { getMomentAtlasEnvPath, writeTextFile } from "~/data/filesystem";
import { getDb } from "~/data/sql";
import { atlasFetch } from "~/utils/atlas";

/*
    This handles all the Atlas state the frondend needs to render the UI.
    Secrets and Credentials should NOT be stored here. That is stored in the Atlas proxy itself.

    Most of this state should be configuration we want to allow the user to change via the UI.
*/

type AtlasSetting = {
	id: string;
	type: "InputSwitch";
	enabled: boolean;
};

export const getDisplayName = (id: string) => {
	const mapping = new Map<string, string>([
		["proxy_github", "Proxy Github:"],
		["proxy_aws", "Proxy AWS:"],
	]);

	return mapping.get(id) || id;
};

export const getSortedSettingsList = (settings: Map<string, AtlasSetting>) => {
	const sortedSettings = Array.from(settings.values()).sort((a, b) => {
		return a.id.localeCompare(b.id);
	});

	return sortedSettings;
};

const initialSettings = new Map<string, AtlasSetting>([
	["proxy_github", { id: "proxy_github", type: "InputSwitch", enabled: false }],
	["proxy_aws", { id: "proxy_aws", type: "InputSwitch", enabled: false }],
]);

const initialAtlasState: AtlasState = {
	status: "NOT_RUNNING",
	settings: initialSettings,
	envVars: {
		data: [],
	},
};

const AtlasConfigSchema = z.object({
	kind: z.literal("Atlas"),
	apiVersion: z.literal("moment.dev/atlas/v1alpha1"),
	metadata: z.object({ name: z.string(), uid: z.string() }),
});

const AtlasAwsAdapterSchema = z.object({
	kind: z.literal("AWS"),
	apiVersion: z.literal("moment.dev/adapters/v1alpha1"),
	metadata: z.object({ name: z.string() }),
	spec: z.object({
		profiles: z.array(
			z.object({
				name: z.string(),
				accessKeyId: z.string(),
				secretAccessKey: z.string(),
				sessionToken: z.string().optional(),
			})
		),
	}),
});

const AtlasHttpAdapterSchema = z.object({
	kind: z.literal("HTTP"),
	apiVersion: z.literal("moment.dev/adapters/v1alpha1"),
	metadata: z.object({ name: z.string() }),
	spec: z.object({
		baseUrl: z.string(),
		headers: z.array(z.object({ name: z.string(), value: z.string() })),
	}),
});

const AtlasInstanceSchema = z.object({
	config: AtlasConfigSchema.readonly(),
	adapters: z.array(AtlasAwsAdapterSchema.readonly().or(AtlasHttpAdapterSchema.readonly())),
});

const envVarRegex = new RegExp(/\${{([^}].*?)}}/, "g");

export const AtlasEnvVarSchema = z.object({
	key: z.string().min(1),
	value: z.string(),
});

export type AtlasEnvVarType = z.infer<typeof AtlasEnvVarSchema>;
export type AtlasAwsAdapterType = z.infer<typeof AtlasAwsAdapterSchema>;
export type AtlasHttpAdapterType = z.infer<typeof AtlasHttpAdapterSchema>;
export type AtlasInstanceAdaptersType = z.infer<typeof AtlasInstanceSchema>["adapters"];

export type AtlasEnvVarView = {
	adapterName: string;
	envVarName: string;
	adapterDesc: string;
	adapterKind: string;
};

type AtlasState = {
	status: "RUNNING" | "NOT_RUNNING";
	settings: Map<string, AtlasSetting>;
	envVars: {
		data: AtlasEnvVarView[];
	};
};

export const atlasMachine = setup({
	types: {
		context: {} as { state: AtlasState },
		events: {} as
			| { type: "idle"; output: AtlasState }
			| { type: "persistSettings"; output: AtlasState }
			| { type: "persistEnvVars"; output: AtlasState; input: Array<AtlasEnvVarType> },
	},
	actors: {
		loadAtlasState: fromPromise(async () => {
			const db = await getDb();
			const query = sql`
                SELECT * FROM atlas_settings
            `;

			const atlasState = initialAtlasState;
			const dbSettings = await db?.select<unknown[]>(query.sql, query.values);

			if (dbSettings === undefined) {
				return atlasState;
			}

			dbSettings.forEach((setting) => {
				const id = setting?.["id"];
				const enabled = setting?.["enabled"] === "true";
				if (id && atlasState.settings.has(id)) {
					const setting = atlasState.settings.get(id);
					if (!setting) {
						return;
					}
					setting.enabled = enabled;
					atlasState.settings.set(id, setting);
				}
			});

			try {
				const c = await atlasFetch("/v1alpha1/instance");

				if (!c.ok) {
					console.log("unsuccessful atlas response to instance metadata");
					throw Error("could not connect to Atlas");
				}

				const meta = await c.json();

				const parsed = AtlasInstanceSchema.safeParse(meta);

				if (!parsed.success) {
					console.log("failed to parse atlas instance response", parsed.error);
					throw Error("invalid response from Atlas");
				}

				const adapters = parsed.data.adapters;
				const adapterArray = adapters.flatMap((adapter) => {
					if (adapter.kind == "HTTP") {
						const searchString = adapter.spec.headers
							.flatMap((h) => [h.name, h.value])
							.join(" ");
						const matches = [...searchString.matchAll(envVarRegex)];
						const envVarNames = matches.flatMap((match) => match.slice(1));
						return envVarNames.map((ev) => {
							return {
								adapterName: adapter.metadata.name,
								envVarName: ev,
								adapterDesc: adapter.spec.baseUrl,
								adapterKind: adapter.kind.toString(),
							};
						});
					} else if (adapter.kind === "AWS") {
						return adapter.spec.profiles.flatMap((profile) => {
							const searchString = `${profile.accessKeyId} ${profile.secretAccessKey} ${profile.sessionToken}`;
							const matches = [...searchString.matchAll(envVarRegex)];
							const envVarNames = matches.flatMap((match) => match.slice(1));
							return envVarNames.map((ev) => {
								return {
									adapterName: adapter.metadata.name,
									envVarName: ev,
									adapterDesc: profile.name,
									adapterKind: adapter.kind.toString(),
								};
							});
						});
					} else {
						console.log(`unhandled atlas adapter ${adapter}`);
						return [];
					}
				});

				atlasState.envVars = {
					data: adapterArray,
				};
			} catch (e) {
				console.error("failed to fetch atlas config", e);
				throw e;
			}

			return atlasState;
		}),
		persistAtlasSettingsState: fromPromise(
			async ({ input: { state } }: { input: { state: AtlasState } }) => {
				const db = await getDb();
				const queries: Sql[] = [];

				state.settings.forEach((setting) => {
					const query = sql`
                        INSERT INTO atlas_settings (id, type, enabled)
                        VALUES (${setting.id}, ${setting.type}, ${setting.enabled})
                        ON CONFLICT (id) DO UPDATE SET enabled = ${setting.enabled}
                    `;
					queries.push(query);
				});

				await db
					?.execute(
						queries.flatMap((q) => q.sql).join(";"),
						queries.flatMap((q) => q.values)
					)
					.catch((e) => console.error("error on save", e));
				return state;
			}
		),
		persistAtlasEnvVarsState: fromPromise(
			async ({ input }: { input: Array<AtlasEnvVarType> }) => {
				// DO NOT PRINT THE ENV VAR VALUES
				const envfp = await getMomentAtlasEnvPath();

				console.debug("writing to path " + envfp);
				const currentVarsStream = await fs.readTextFileLines(envfp, {
					baseDir: fs.BaseDirectory.Home,
				});

				// DO NOT PRINT THE ENV VAR VALUES
				const currentVars = new Map<string, string>();
				for await (const line of currentVarsStream) {
					const [key, ...rest] = line.split("=");
					const value = rest.join("=");
					if (typeof key === "string" && !isEmpty(key)) {
						currentVars.set(key, value);
					}
				}

				// DO NOT PRINT THE ENV VAR VALUES
				for (const envvar of input) {
					if (!isEmpty(envvar.value)) {
						currentVars.set(envvar.key.trim(), envvar.value.trim());
					}
				}

				// DO NOT PRINT THE ENV VAR VALUES
				const kvs: string[] = [];
				for (const [key, value] of currentVars) {
					kvs.push(`${key}=${value}\n`);
				}

				// DO NOT PRINT THE ENV VAR VALUES
				const contents = kvs.join("");
				await writeTextFile(envfp, contents);
				console.debug("atlas env var file has been updated");
			}
		),
	},
	actions: {
		assignToContext: assign({
			state: ({ event }) => {
				return {
					...event.output,
					settings: new Map(event.output.settings),
				};
			},
		}),
	},
}).createMachine({
	id: "atlas",
	context: { state: initialAtlasState },
	initial: "loading",
	states: {
		loading: {
			invoke: {
				src: "loadAtlasState",
				onDone: {
					target: "idle",
					actions: ["assignToContext"],
				},
				onError: {
					target: "error",
					actions: [
						({ event }) => {
							console.error(event.error);
						},
					],
				},
			},
		},
		persistSettings: {
			invoke: {
				src: "persistAtlasSettingsState",
				input: ({ event }) => {
					return { state: event.output };
				},
				onDone: {
					target: "idle",
					actions: ["assignToContext"],
				},
				onError: {
					target: "error",
					actions: [
						({ event }) => {
							console.error(event.error);
						},
					],
				},
			},
		},
		persistEnvVars: {
			invoke: {
				src: "persistAtlasEnvVarsState",
				input: ({ event }) => {
					if (event.type !== "persistEnvVars") {
						throw new Error("invalid event type");
					}
					return event.input;
				},
				onDone: {
					target: "idle",
				},
				onError: {
					target: "error",
					actions: [
						({ event }) => {
							console.error(event.error);
						},
					],
				},
			},
		},
		idle: {
			on: {
				persistSettings: "persistSettings",
				persistEnvVars: "persistEnvVars",
			},
		},
		error: {
			type: "final",
		},
	},
});
